import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { Observable, Subscription } from "rxjs";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { OrderFacade } from "@spartacus/order/root";
import { CmsService, RoutingService } from "@spartacus/core";
import { LAUNCH_CALLER } from "@spartacus/storefront";
import { ActiveCartFacade, Cart } from "@spartacus/cart/base/root";
import { filter, map, take, tap } from "rxjs/operators";
import { UserAccountFacade } from '@spartacus/user/account/root';
import { GenPaymentTypeEnum } from 'src/app/generac/enums/payment-type.enum';
import { GenUserGroup } from 'src/app/generac/enums/user.enum';
import { GenLaunchDialogService } from 'src/app/generac/services/launch-dialog.service';
import { GenOrderService } from 'src/app/generac/services/order.service';
import { GenOrderDocumentErrorService } from 'src/app/generac/services/order-document-error.service';

@Component({
  selector: 'gen-place-order',
  templateUrl: './checkout-place-order.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GenCheckoutPlaceOrderComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  cart: Cart;
  isTestPlaceOrder$!: Observable<boolean>;
  isPlaceOrderBtnAvailable: boolean = true;
  termsAndConditionsUrl$!: Observable<string>;

  checkoutSubmitForm: UntypedFormGroup = this.fb.group({
    termsAndConditions: [false, Validators.requiredTrue],
  });

  get termsAndConditionInvalid(): boolean {
    return this.checkoutSubmitForm.invalid;
  }

  constructor(
    protected orderFacade: OrderFacade,
    protected routingService: RoutingService,
    protected fb: UntypedFormBuilder,
    protected launchDialogService: GenLaunchDialogService,
    protected vcr: ViewContainerRef,
    protected activeCartFacade: ActiveCartFacade,
    private genOrderService: GenOrderService,
    private cdr: ChangeDetectorRef,
    private cmsService: CmsService,
    private documentErrorService: GenOrderDocumentErrorService,
    private userAccount?: UserAccountFacade,
  ) {
  }

  ngOnInit(): void {
    this.getCart();
    this.getUserAccount();
    this.subscribeToPlaceOrderBtnState();
    this.termsAndConditionsUrl$ = this.cmsService
      .getComponentData<any>('termsAndConditionsCheckoutLink')
      .pipe(map(component => component?.url));
  }

  ngOnDestroy(): void {
    this.launchDialogService.closeDialog('close', LAUNCH_CALLER.PLACE_ORDER_SPINNER);
    this.launchDialogService.closeDialog('close', 'PAY_BY_DELEGO');
    this.genOrderService.setIsPlaceOrderBtnAvailable(true);
    this.subscription.unsubscribe();
  }

  submitForm(): void {
    if (this.requiresOrderDocuments) {
      this.documentErrorService.showRequiredError();
      this.routingService.go('/checkout/delivery-address', { fragment: 'orderDocuments' });
      return;
    }

    if (this.checkoutSubmitForm.valid) {
      if (this.cart.paymentType.code === GenPaymentTypeEnum.CARD) {
        this.openDelegoModal();
      } else {
        this.placeOrder();
        const placedOrder = this.launchDialogService.openDialog(
          LAUNCH_CALLER.PLACE_ORDER_SPINNER,
          undefined,
          this.vcr
        );

        if (placedOrder) {
          placedOrder.pipe(take(1)).subscribe();
        }
      }
    } else {
      this.checkoutSubmitForm.markAllAsTouched();
    }
  }

  openDelegoModal(): void {
    const dialog = this.launchDialogService.openDialog(
      'PAY_BY_DELEGO',
      undefined,
      this.vcr,
    );

    if (dialog) {
      this.genOrderService.setIsPlaceOrderBtnAvailable(false);
      dialog
        .subscribe();
    }

    this.subscription.add(
      this.launchDialogService.dialogClose
        .pipe(
          tap((res) => {
            if (res == 'placeOrder') {
              const placedOrder = this.launchDialogService.openDialog(
                LAUNCH_CALLER.PLACE_ORDER_SPINNER,
                undefined,
                this.vcr
              );

              if (placedOrder) {
                placedOrder.pipe(take(1)).subscribe();
              }
            }

            this.genOrderService.setIsPlaceOrderBtnAvailable(res == 'destroy');
          })
        )
        .subscribe()
    );
  }

  placeOrder(): void {
    this.genOrderService.placeOrder(this.checkoutSubmitForm.valid, false);
  }

  private subscribeToPlaceOrderBtnState() {
    this.subscription.add(
      this.genOrderService.isPlaceOrderBtnAvailable.asObservable()
        .subscribe(isPlaceOrderBtnAvailable => {
          this.isPlaceOrderBtnAvailable = isPlaceOrderBtnAvailable;
          this.cdr.markForCheck();
        })
    );
  }

  private getCart(): void {
    this.activeCartFacade.getActive()
      .pipe(
        take(1),
        tap((cart: any) => this.cart = cart)
      )
      .subscribe();
  }

  private getUserAccount(): void {
    this.isTestPlaceOrder$ = this.userAccount.get()
      .pipe(
        filter(user => !!user),
        map(user => user?.roles?.includes(GenUserGroup.testorderplacegroup))
      );
  }

  getOrderConfirmationText(isTestOrder: boolean, cart: any): string {
    if (isTestOrder) {
      return cart?.wellsFargoCart
        ? 'checkoutOrderConfirmation.placeTestWfOrder'
        : 'checkoutOrderConfirmation.placeTestOrder';
    }
    return cart?.wellsFargoCart
      ? 'checkoutReview.placeWfOrder'
      : 'checkoutReview.placeOrder';
  }

  get requiresOrderDocuments(): boolean {
    return this.cart?.orderDocumentsRequired === true && !this.cart?.orderDocuments?.length;
  }
}
